/**
 * Console mobile de debug embarquée (Eruda).
 *
 * Active une console JavaScript flottante directement dans l'app, accessible
 * sur smartphone/tablette. Permet à Léonce (Champion digital) de :
 *   - voir les erreurs JS en direct
 *   - inspecter les requêtes réseau (XHR / fetch)
 *   - capturer les logs console pour les remonter
 *
 * Activation (3 méthodes, la première qui correspond suffit) :
 *   1. URL : ajouter `?debug=1` à l'URL → console activée
 *   2. localStorage : `localStorage.setItem('sada_debug', '1')` → persistant
 *   3. Compte connecté : si le téléphone connecté est `0700000099` (Léonce)
 *      → activation automatique
 *
 * Désactivation : retirer `?debug=1` ou faire
 *   `localStorage.removeItem('sada_debug')` puis recharger.
 */

const ERUDA_CDN = 'https://cdn.jsdelivr.net/npm/eruda@3.0.1/eruda.min.js';

const isLeonceAccount = () => {
  try {
    const raw = localStorage.getItem('dermascan_user');
    if (!raw) return false;
    const u = JSON.parse(raw);
    return u?.phone === '0700000099';
  } catch (_) {
    return false;
  }
};

const shouldEnable = () => {
  // Désactivé en SSR
  if (typeof window === 'undefined') return false;
  // 1. URL param
  if (new URLSearchParams(window.location.search).get('debug') === '1') return true;
  // 2. localStorage
  if (localStorage.getItem('sada_debug') === '1') return true;
  // 3. Compte Léonce (test account)
  if (isLeonceAccount()) return true;
  return false;
};

const loadEruda = () => new Promise((resolve, reject) => {
  if (window.eruda) return resolve(window.eruda);
  const s = document.createElement('script');
  s.src = ERUDA_CDN;
  s.async = true;
  s.onload = () => resolve(window.eruda);
  s.onerror = () => reject(new Error('Eruda failed to load'));
  document.head.appendChild(s);
});

export const initDebugConsole = async () => {
  if (!shouldEnable()) return;
  try {
    const eruda = await loadEruda();
    if (eruda && !eruda._isInit) {
      eruda.init();
      // Persiste l'activation pour la session
      try { localStorage.setItem('sada_debug', '1'); } catch (_) { /* ignore */ }
      // eslint-disable-next-line no-console
      console.info('[SADA Debug] Console mobile activée — bouton flottant en bas à droite.');
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[SADA Debug] Impossible de charger Eruda :', err);
  }
};

/**
 * Permet d'activer/désactiver manuellement (utilisé par les agents
 * pour activer le debug ponctuellement sans changer l'URL).
 */
export const toggleDebugConsole = () => {
  const isOn = localStorage.getItem('sada_debug') === '1';
  if (isOn) {
    localStorage.removeItem('sada_debug');
    if (window.eruda) window.eruda.destroy();
  } else {
    localStorage.setItem('sada_debug', '1');
    initDebugConsole();
  }
};
